import boxMoney from '@/assets/png/box-reward-money.png';
import boxReward from '@/assets/png/box-reward.png';
import SpinLoading from '@/components/loading/SpinLoading';
import { CLAIM_CACHE_TTL, storage } from '@/utils/storage';
import clsx from 'clsx';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CampaignTask, UserTask } from '../api';
import { useClaimCampaign } from '../claim';
import type { RewardType } from './types';

// --- Types ---
interface DayTileProps {
    dayData: Partial<UserTask> & {
        isClaimed: boolean;
        isCompleted: boolean;
        isDisplayReward: boolean;
        isDisplayClaim: boolean;
        info: CampaignTask | undefined;
    };
    mutate: () => void; // Thêm prop onClick để xử lý sự kiện từ cha
}

// --- Sub-components (Tách nhỏ để dễ quản lý) ---

const CompletedIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
    >
        <title>Completed</title>
        <path
            d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM15.535 8.381L10.585 13.331L8.465 11.21C8.37216 11.1171 8.26192 11.0434 8.14059 10.9931C8.01926 10.9428 7.8892 10.9168 7.75785 10.9168C7.49258 10.9167 7.23814 11.022 7.0505 11.2095C6.86286 11.397 6.75739 11.6514 6.7573 11.9166C6.7572 12.1819 6.86249 12.4364 7.05 12.624L9.808 15.382C9.91015 15.4842 10.0314 15.5653 10.1649 15.6206C10.2984 15.6759 10.4415 15.7044 10.586 15.7044C10.7305 15.7044 10.8736 15.6759 11.0071 15.6206C11.1406 15.5653 11.2618 15.4842 11.364 15.382L16.95 9.796C17.1376 9.60836 17.2431 9.35386 17.2431 9.0885C17.2431 8.82314 17.1376 8.56864 16.95 8.381C16.7624 8.19336 16.5079 8.08794 16.2425 8.08794C15.9771 8.08794 15.7226 8.19336 15.535 8.381Z"
            fill="#22FF00"
        />
    </svg>
);

const DashedPlaceholder = () => (
    <div className="w-5 h-5 border border-dashed border-[#333] rounded-full" />
);

const RewardIcon = ({ type }: { type: RewardType }) => {
    if (type === 'GIFTBOX') {
        return (
            <img
                src={boxReward}
                className="w-8 h-8 object-contain"
                alt="Gift"
            />
        );
    }
    // Default to GOLD/CLAIM box money
    return (
        <img src={boxMoney} className="w-10 h-10 object-contain" alt="Money" />
    );
};

// --- Main Component ---

const DayTile: React.FC<DayTileProps> = ({ dayData, mutate }) => {
    const { t, i18n } = useTranslation();
    const { isCompleted, isDisplayReward, isDisplayClaim, isClaimed, info } =
        dayData;

    const { claim, isLoading } = useClaimCampaign();

    const [isClaim, setIsClaim] = useState(false);

    const claimReward = isClaimed || isClaim;

    const handleClaim = useCallback(async () => {
        if (
            !dayData.info?.task_params.token_reward ||
            !dayData.call_data ||
            !dayData.nonce ||
            !dayData.signature
        ) {
            return;
        }
        const tx = await claim({
            address: dayData.info?.task_params.token_reward,
            callData: dayData.call_data,
            nonce: dayData.nonce,
            signal: dayData.signature,
        });

        if (tx) {
            storage.setWithExpiry(
                `campaign_volume_claimed_${dayData.id}`,
                true,
                CLAIM_CACHE_TTL
            );

            setIsClaim(true);
        }
    }, [
        claim,
        dayData.call_data,
        dayData.id,
        dayData.info?.task_params.token_reward,
        dayData.nonce,
        dayData.signature,
    ]);

    // 1. Xác định nội dung Icon chính (Phần trên)
    const renderMainIcon = () => {
        if (isCompleted && !isDisplayReward) {
            return <CompletedIcon />;
        }
        if (isDisplayReward) {
            return <RewardIcon type="GOLD" />;
        }
        return <DashedPlaceholder />;
    };

    // 2. Xác định nội dung Footer (Phần dưới: Text hoặc Button)
    const renderFooter = () => {
        // ƯU TIÊN 1 — Nếu đã claim reward → luôn hiển thị text Claimed
        if (claimReward) {
            return (
                <div className="text-[#808080] text-xs font-semibold w-full bg-[#141414] px-4 py-2 text-center truncate uppercase">
                    {t('Claimed')}
                </div>
            );
        }

        // ƯU TIÊN 2 — Nếu đang hiển thị nút Claim
        if (isDisplayReward && isDisplayClaim) {
            return (
                <div className="flex w-full flex-col justify-center items-center bg-[#141414] py-1.5">
                    <button
                        type="button"
                        className="bg-white text-black text-xs uppercase font-bold px-2 py-0.5 rounded-full shadow-sm active:scale-95 transition-transform"
                        onClick={handleClaim}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <SpinLoading className="!h-4 !w-4" />
                        ) : (
                            t('Claim')
                        )}
                    </button>
                </div>
            );
        }

        // ƯU TIÊN 3 — Hiển thị title mặc định
        return (
            <div className="text-white text-xs font-semibold w-full bg-[#141414] px-4 py-2 text-center truncate">
                {info?.title?.[i18n.language] || info?.title?.en}
            </div>
        );
    };

    return (
        <div
            className={clsx(
                'flex flex-col items-center justify-between',
                'h-[90px] pt-2 rounded-xl overflow-hidden relative',
                'bg-[#0D0D0D] border border-[#1F1F1F]',
                'cursor-pointer transition duration-200 hover:bg-[#1a1a1a] active:opacity-90'
            )}
        >
            {/* Phần Icon chính căn giữa khoảng trống còn lại */}
            <div className="flex-1 flex items-center justify-center w-full pb-1">
                {renderMainIcon()}
            </div>

            {/* Phần Footer luôn nằm đáy */}
            <div className="w-full">{renderFooter()}</div>
        </div>
    );
};

export default memo(DayTile);
